/* Flux product homepage ("/" when Settings → Public site → "Product homepage" is on; App.js HomeRoute).
 * Real material only: screenshots of this panel (client/public/home/*.webp, taken from a real BO3 with
 * the private parts left out), this panel's own live / recent matches, the commands and answers of the
 * About page (same translation keys). No logos, numbers or quotes nobody gave. Flux is not free: no price. */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicHeader from '../PublicHeader';
import Footer from '../Footer';
import usePageTitle from '../../utils/usePageTitle';
import { publicApi, TeamLogo, fmtDay } from '../public/pubUi';
import '../public/publicPages.css';
import './home.css';

const DISCORD_URL = 'https://discord.gg/PdghFH7TU8';
const EMAIL = 'kancha@lotgaming.xyz';

// a match row: teams, score (series for BO3 / BO5), state, link to its page
function MatchRow({ m }) {
  const { t, i18n } = useTranslation();
  const bo = parseInt(m.best_of, 10) || 1;
  const s1 = bo > 1 ? (m.map_wins_team1 || 0) : (m.score_team1 || 0);
  const s2 = bo > 1 ? (m.map_wins_team2 || 0) : (m.score_team2 || 0);
  const live = m.status === 'live';
  return (
    <Link to={`/match/${m.id}`} className="lnd-match">
      <span className={`lnd-match-state${live ? ' live' : ''}`}>{live ? t('home.live', 'Live') : fmtDay(m.end_time || m.created_at, i18n.language)}</span>
      <span className="lnd-match-team one">
        <span className={s1 > s2 && !live ? 'won' : ''}>{m.team1_name}</span>
        <TeamLogo id={m.team1_id} name={m.team1_name} avatarV={m.team1_avatar_v} size={40} />
      </span>
      <span className="lnd-match-score">{s1}<span aria-hidden="true">:</span>{s2}</span>
      <span className="lnd-match-team two">
        <TeamLogo id={m.team2_id} name={m.team2_name} avatarV={m.team2_avatar_v} size={40} />
        <span className={s2 > s1 && !live ? 'won' : ''}>{m.team2_name}</span>
      </span>
      <span className="lnd-match-bo">BO{bo}</span>
    </Link>
  );
}

function PanelMatches() {
  const { t } = useTranslation();
  const [state, setState] = useState({ list: null, live: false });
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const live = (await publicApi.get('/api/matches', { params: { status: 'live', limit: 4 } })).data?.data || [];
        const list = live.length ? live : ((await publicApi.get('/api/matches', { params: { status: 'finished', limit: 4 } })).data?.data || []);
        if (on) setState({ list, live: live.length > 0 });
      } catch (_) { if (on) setState({ list: [], live: false }); }
    })();
    return () => { on = false; };
  }, []);
  // nothing played on this panel yet: the section isn't shown
  if (!state.list || !state.list.length) return null;
  return (
    <section className="lnd-sec" aria-labelledby="lnd-matches">
      <div className="lnd-wrap lnd-split-head">
        <h2 id="lnd-matches">{state.live ? t('home.matchesLive', 'Live on this panel') : t('home.matchesRecent', 'Played on this panel')}</h2>
        <Link to="/matches" className="lnd-link">{t('home.allMatches', 'All matches')}</Link>
      </div>
      <div className="lnd-wrap">
        <div className="lnd-matches">{state.list.map(m => <MatchRow key={m.id} m={m} />)}</div>
      </div>
    </section>
  );
}

function Viewers() {
  const { t } = useTranslation();
  const views = [
    { key: 'match', label: t('home.viewMatch', 'Match page'), src: '/home/public-match.webp', w: 1764, h: 1050,
      alt: t('home.viewMatchAlt', 'Public match page: SINNERS 13, Eternal Fire 6 on Mirage, player stats and the kill feed'),
      note: t('home.viewMatchNote', 'Live score, every player’s stats and the kill feed, round by round.') },
    { key: 'board', label: t('home.viewBoard', 'Season leaderboard'), src: '/home/leaderboard.webp', w: 1572, h: 960,
      alt: t('home.viewBoardAlt', 'Season leaderboard of DraculaN #7 with the top three players and the full table'),
      note: t('home.viewBoardNote', 'The whole season ranked by HLTV 2.0, with filters by map, team and stage.') },
  ];
  const [cur, setCur] = useState('match');
  const v = views.find(x => x.key === cur);
  return (
    <section className="lnd-sec band" aria-labelledby="lnd-viewers">
      <div className="lnd-wrap lnd-split-head">
        <div>
          <h2 id="lnd-viewers">{t('home.viewersHead', 'A public page for every match')}</h2>
          <p className="lnd-lead">{t('home.viewersLead', 'Public pages, no login: share one link per match.')}</p>
        </div>
        <div className="lnd-seg" role="group" aria-label={t('home.viewersHead', 'A public page for every match')}>
          {views.map(x => (
            <button key={x.key} type="button" aria-pressed={cur === x.key} className={cur === x.key ? 'on' : ''} onClick={() => setCur(x.key)}>{x.label}</button>
          ))}
        </div>
      </div>
      <div className="lnd-wrap">
        <figure className="lnd-shot wide">
          <img src={v.src} width={v.w} height={v.h} alt={v.alt} loading="lazy" />
          <figcaption>{v.note}</figcaption>
        </figure>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useTranslation();
  usePageTitle(t('seo.homeFullTitle', 'Flux - CS2 Tournament Panel: Live Matches, Scores and Stats'), true);

  // each way in, with the real line it runs (code stays untranslated)
  const links = [
    { key: 'rcon', title: 'RCON', code: 'exec warmup.cfg', text: t('home.rcon', 'Configs, ready checks, pauses, restores and player names.') },
    { key: 'log', title: t('home.logTitle', 'Log address'), code: 'logaddress_add_http "http://<panel>:3333/<secret>"', text: t('home.log', 'Every kill, round and chat line reaches the panel live.') },
    { key: 'lan', title: t('home.lanTitle', 'LAN agent'), code: 'curl -fsSL <panel>/agent/install.sh | sudo bash', text: t('home.lan', 'Venue servers dial out to Flux. Nothing to port-forward.') },
  ];
  const commands = [
    { who: t('home.whoPlayers', 'Players'), list: [
      ['!ready', t('about.commands.ready', 'Player ready during warmup')],
      ['!tech', t('about.commands.tech', 'Tech pause')],
      ['!tac', t('about.commands.tac', 'Tactical timeout')],
    ] },
    { who: t('home.whoAdmins', 'Admins'), list: [
      ['!restore N', t('about.commands.restore', 'Admin restores round N')],
      ['!fp / !fup', t('about.commands.fp', 'Admin force pause or unpause')],
    ] },
  ];

  const actions = (
    <div className="lnd-actions">
      <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="lnd-btn primary">{t('nav.joinDiscord', 'Join Discord')}</a>
      <a href={`mailto:${EMAIL}?subject=${encodeURIComponent('Flux access')}`} className="lnd-btn">{t('home.email', 'Email us')}</a>
    </div>
  );

  return (
    <div className="lnd">
      <PublicHeader />
      <main>
        {/* Hero: the real admin page of a BO3 */}
        <section className="lnd-hero" aria-labelledby="lnd-title">
          <div className="lnd-wrap lnd-hero-grid">
            <div className="lnd-hero-text">
              <h1 id="lnd-title">{t('home.title', 'Run CS2 matches from one page')}</h1>
              <p className="lnd-lead">{t('home.lead', 'Veto, ready-up, pauses and round restores on your own servers. No plugin.')}</p>
              {actions}
              <p className="lnd-terms">{t('home.terms', 'Access by request while Flux is in beta.')}</p>
            </div>
            <figure className="lnd-shot hero">
              {/* phones get the match controls alone: the whole page shrunk to 360px can't be read */}
              <picture>
                <source media="(max-width: 40rem)" srcSet="/home/admin-match-phone.webp" width="533" height="780" />
                <img src="/home/admin-match.webp" width="1640" height="900" fetchpriority="high"
                  alt={t('home.heroAlt', 'The admin match page of a BO3: score, map, players and the match controls')} />
              </picture>
              <figcaption>{t('home.heroCaption', 'The admin page of a finished BO3, SINNERS against Eternal Fire.')}</figcaption>
            </figure>
          </div>
        </section>

        <PanelMatches />

        {/* How it connects: no plugin */}
        <section className="lnd-sec connect" aria-labelledby="lnd-connect">
          <div className="lnd-wrap lnd-split-head">
            <div>
              <h2 id="lnd-connect">{t('home.connectHead', 'How Flux talks to your server')}</h2>
              <p className="lnd-lead">{t('home.noPlugin', 'No SourceMod, no CounterStrikeSharp, nothing to install on the server.')}</p>
            </div>
          </div>
          <div className="lnd-wrap">
            <dl className="lnd-ways">
              {links.map(l => (
                <div key={l.key} className="lnd-way">
                  <dt>{l.title}</dt>
                  <dd className="lnd-way-code"><code>{l.code}</code></dd>
                  <dd>{l.text}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <Viewers />

        {/* In-game commands */}
        <section className="lnd-sec" aria-labelledby="lnd-commands">
          <div className="lnd-wrap lnd-split-head">
            <h2 id="lnd-commands">{t('home.commandsHead', 'What players type in chat')}</h2>
            <Link to="/about" className="lnd-link">{t('home.allCommands', 'Every command')}</Link>
          </div>
          <div className="lnd-wrap lnd-cmds">
            {commands.map(g => (
              <div key={g.who}>
                <h3>{g.who}</h3>
                <dl>
                  {g.list.map(([cmd, what]) => (
                    <div key={cmd} className="lnd-cmd"><dt><code>{cmd}</code></dt><dd>{what}</dd></div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </section>

        {/* Close: the hero's actions again (the questions live on the About page) */}
        <section className="lnd-close" aria-labelledby="lnd-close">
          <div className="lnd-wrap lnd-close-grid">
            <div>
              <h2 id="lnd-close">{t('home.closeHead', 'Run your next BO3 on Flux')}</h2>
              <p className="lnd-lead">{t('home.closeLead', 'Tell us about your event and we’ll get your panel set up.')}</p>
              <p className="lnd-close-more"><Link to="/about" className="lnd-link">{t('home.aboutLink', 'How a match night goes')}</Link></p>
            </div>
            {actions}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
